import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import { Mail, Phone, MapPin, Send, CheckCircle2, AlertCircle, MessageCircle } from "lucide-react";

const ContactUs = () => {
  const formRef = useRef();

  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const sendEmail = async (e) => {
    e.preventDefault();

    setIsLoading(true);
    setSuccessMessage("");
    setErrorMessage("");

    const formData = new FormData(formRef.current);
    const payload = {
      fullName: formData.get("full_name"),
      email: formData.get("email"),
      phone: formData.get("phone") || "",
      message: formData.get("message"),
    };

    const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:6060/api/v1";

    try {
      // 1. Save directly to SphereX Backend Database for Admins
      const res = await fetch(`${API_BASE}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data?.message || "Failed to submit message");
      }

      // 2. Also attempt EmailJS dispatch as notification backup (non-blocking)
      try {
        emailjs.sendForm(
          "service_129fw02",
          "template_p9kx5tr",
          formRef.current,
          "aT1fZJNT5HiSW-MRI"
        ).catch((err) => console.log("EmailJS backup notice:", err));
      } catch (e) {
        // ignore emailjs error since database saved successfully
      }

      setSuccessMessage(data?.message || "Thanks for reaching out! Our team will get back to you shortly.");
      formRef.current.reset();
    } catch (err) {
      console.error("Contact submission error:", err);
      setErrorMessage(err.message || "Something went wrong. Please try again or reach us via WhatsApp.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="py-20 bg-slate-50 border-t border-gray-100 px-4 sm:px-6 lg:px-8" id="support">
      <div className="max-w-6xl mx-auto">
        
        {/* Section Heading */}
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 text-purple-700 text-xs font-bold uppercase tracking-wider mb-3">
            <MessageCircle className="w-3.5 h-3.5" />
            <span>We Are Here For You</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 tracking-tight">
            Have Questions? Let's Talk
          </h2>
          <p className="mt-3 text-base text-gray-600">
            Need help with a trade or have an inquiry? Our friendly team is available 24/7.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left: Contact Info (5 cols) */}
          <div className="lg:col-span-5 bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-100 space-y-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Get Instant Support</h3>
              <p className="text-sm text-gray-500 mt-1">
                Reach out through any of our official communication channels.
              </p>
            </div>

            <div className="space-y-4 pt-2">
              <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-purple-50/60 border border-purple-100/80">
                <Phone className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-purple-900">
                    Phone & WhatsApp
                  </h4>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5">+234 702 544 6361</p>
                  <p className="text-xs text-gray-500">Available 24 hours daily</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-purple-50/60 border border-purple-100/80">
                <Mail className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-purple-900">
                    Email Address
                  </h4>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5">support@thespherex.com</p>
                  <p className="text-xs text-gray-500">Quick responses within minutes</p>
                </div>
              </div>

              <div className="flex items-start gap-3.5 p-3.5 rounded-xl bg-purple-50/60 border border-purple-100/80">
                <MapPin className="w-5 h-5 text-purple-600 shrink-0 mt-0.5" />
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-purple-900">
                    Office Location
                  </h4>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5">Onike, Yaba, Lagos, Nigeria</p>
                  <p className="text-xs text-gray-500">Registered financial platform</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Clean Message Form (7 cols) */}
          <div className="lg:col-span-7 bg-white p-6 sm:p-8 rounded-2xl shadow-sm border border-gray-100">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Send Us a Direct Message</h3>

            <form ref={formRef} onSubmit={sendEmail} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Your Full Name
                  </label>
                  <input
                    type="text"
                    name="full_name"
                    placeholder="e.g. Adeola Johnson"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-purple-600 focus:ring-2 focus:ring-purple-100 text-sm text-gray-900 bg-gray-50/50 transition-all"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    name="email"
                    placeholder="e.g. adeola@example.com"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-purple-600 focus:ring-2 focus:ring-purple-100 text-sm text-gray-900 bg-gray-50/50 transition-all"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Phone Number (Optional)
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="e.g. +234 801 234 5678"
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-purple-600 focus:ring-2 focus:ring-purple-100 text-sm text-gray-900 bg-gray-50/50 transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Your Message
                </label>
                <textarea
                  name="message"
                  placeholder="Tell us what you need help with..."
                  required
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-purple-600 focus:ring-2 focus:ring-purple-100 text-sm text-gray-900 bg-gray-50/50 transition-all resize-none"
                />
              </div>

              {/* Status alerts */}
              {successMessage && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium">
                  <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{successMessage}</span>
                </div>
              )}

              {errorMessage && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 border border-red-200 text-red-800 text-xs font-medium">
                  <AlertCircle className="w-4 h-4 text-red-600 shrink-0" />
                  <span>{errorMessage}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full flex items-center justify-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-bold py-3 px-6 rounded-xl shadow-md transition-all duration-200 ${
                  isLoading ? "opacity-70 cursor-not-allowed" : "hover:-translate-y-0.5"
                }`}
              >
                <Send className="w-4 h-4" />
                <span>{isLoading ? "Sending Message..." : "Send Message"}</span>
              </button>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
};

export default ContactUs;
